/* eslint-disable max-lines */
import { useCallback, useState, useRef, useMemo } from 'react'

import { defaultAveragesUids, isRowAveragesUid, defaultPreset } from '@aytm/app/insights/dashboard/lib/config'
import { useBlinkControls } from '../useBlink'
import usePool from '@duckness/use-pool'
import InsightsDashboardPool from '@aytm/app/insights/dashboard/duckness/InsightsDashboardPool'
import { poolDispatch } from '@aytm/app/insights/dashboard/duckness/poolDispatch'
import {
  makeItemAddEventAction,
  makeItemDeleteEventAction,
  makeCurrentSplitChangeEventAction,
  changeShowFromToEventAction,
  makeItemDragListenStateEventAction,
  makeItemDragTrackStateEventAction
} from '@aytm/app/insights/dashboard/duckness/ducks/EventDuck/EventDuck.actionCreators'
import { stateSelector, shouldUpdate } from './selectors'

import {
  tagsUpdate,
  editorChange,
  elementToggle,
  imageUpload,
  dispatchItemSelect,
  itemPin,
  sortByAttributeChange,
  metricsChange,
  attributeDelete,
  attributeAdd,
  attributeReplace,
  currentWaveChange,
  currentPresetChange,
  showSectionChange,
  confidenceLevelChange,
  secondaryConfidenceLevelChange,
  chartScaleChange,
  chartTypeChange,
  chartLegendClick,
  colorsChange,
  trackTargetChange,
  trackAttributesChange,
  refreshComponent,
  swapChange
} from './useEvents.handlers'

/**
 * @type {import('./useEvents.types').IUseEvents}
 */
const useEvents = ({
  items,
  fixPinned,
  captionWidth,
  columnWidth,
  gapWidth,
  onFetchData,
  onSubjectSave,
  onSubjectDelete,
  onImageUpload,
  onTagsUpdate
} = {}) => {
  // Current showing items
  const itemsRef = useRef(null)
  itemsRef.current = items || []

  /** Blink info */
  const { startBlinking } = useBlinkControls()

  const {
    blockModel,
    subjects,
    attributes,
    splitOptions,
    selectedSubjects,
    dataFetched,
    serverError,
    editor,
    itemFrom,
    itemTo,
    editorMode,
    isSharedView,
    hasPportalFilters,
    subjectType
  } = usePool(InsightsDashboardPool, stateSelector, shouldUpdate)

  // Block model properties

  /** Current preset */
  const currentPreset = useMemo(() => blockModel?.getObject('currentPreset') || defaultPreset, [blockModel])
  const hideBlankColumns = useMemo(() => blockModel?.hideBlankColumns || false, [blockModel])

  /** Data source (attribute or entity) */
  const trackTarget = useMemo(() => blockModel?.getObject('trackTarget') || 'entity', [blockModel])

  /** Object contains tracking attributes names like { attribute: <...>, entity: <...> } */
  const trackAttributes = useMemo(() => blockModel?.getJS('trackAttributes') || {}, [blockModel])

  /** Current split option */
  const currentSplit = useMemo(() => blockModel?.getObject('currentSplit') || null, [blockModel])

  /** Current swap option */
  const swap = useMemo(() => blockModel?.getObject('swap') || null, [blockModel])

  const mounted = useRef(false)

  const pinnedUids = items.filter(item => item && item.pinned).map(item => item.uid)

  const itemsCount = items.length || 0
  const pinnedCount = currentSplit ? 0 : pinnedUids?.length || 0
  const fixedColumns = fixPinned ? 1 + pinnedCount : 1
  const fixedColumnsWidth = captionWidth + pinnedCount * columnWidth

  const [scrollLeft, setScrollLeft] = useState(0)

  const [dragAttribute, setDragAttribute] = useState(null)

  const [dragItemIndex, setDragItemIndex] = useState(-1)

  const [hoverItemIndex, setHoverItemIndex] = useState(-1)

  const currentEditor = useMemo(
    () =>
      subjects && !currentSplit && editorMode && !isSharedView && (!trackTarget || 'entity' === trackTarget)
        ? editor
        : null,
    [subjects, currentSplit, editorMode, isSharedView, trackTarget, editor]
  )

  // Query callbacks

  const subjectSaveRef = useRef()
  subjectSaveRef.current = onSubjectSave

  const subjectDeleteRef = useRef()
  subjectDeleteRef.current = onSubjectDelete

  const imageUploadRef = useRef()
  imageUploadRef.current = onImageUpload

  const tagsUpdateRef = useRef()
  tagsUpdateRef.current = onTagsUpdate

  const fetchDataRef = useRef()
  fetchDataRef.current = onFetchData

  // Updates

  /**
   * Jump to subject When subject is picked up from search component.
   * Emphasize selected subject by blinking effect
   */
  const jumpToSubject = useCallback(
    event => {
      if (event.uid) {
        // Jump to concept
        const index = items.findIndex(item => item.uid === event.uid)
        if (index >= 0) {
          const newScrollLeft = (columnWidth + gapWidth) * (index - pinnedCount)
          if (!items[index]?.pinned || !fixPinned) {
            setScrollLeft(newScrollLeft)
          }
          setTimeout(() => {
            startBlinking(event.uid)
          }, 50)
        }
      } else if (event.index) {
        // Deprecated: Jump to index
        const { index } = event
        if (index >= 0) {
          const newScrollLeft = (columnWidth + gapWidth) * (index - pinnedCount)
          setScrollLeft(newScrollLeft)
          const item = items[index]
          setTimeout(() => {
            startBlinking(item?.uid)
          }, 50)
        }
      } else if (event.last) {
        // Jump to last
        const newScrollLeft = (columnWidth + gapWidth) * (items.length - pinnedCount)
        setScrollLeft(newScrollLeft)
        const item = items[items.length - 1]
        startBlinking(item?.uid)
      }
    },
    [items, columnWidth, gapWidth, pinnedCount, fixPinned, startBlinking]
  )

  const handleEditorChange = useCallback(
    event => {
      if ('type' in event && 'edit' === event.type) {
        setTimeout(() => {
          jumpToSubject({ index: event.itemIndex })
        }, 10)
      }
      return editorChange(event)
    },
    [jumpToSubject]
  )

  // Items

  /**
   * Append new subject to items list
   */
  const itemAdd = useCallback(
    ({ uid, last, right, left }) => {
      // Dispatch item add event
      poolDispatch(makeItemAddEventAction(uid))
      // Scroll to added item
      if (last) {
        // Jump to last item
        if ('concept' === subjectType) {
          setScrollLeft((columnWidth + gapWidth) * (itemsCount - pinnedCount))
        }
      } else if (right) {
        // One item to right
        setScrollLeft(scrollLeft - columnWidth - gapWidth)
      } else if (left) {
        // One item to left
        setScrollLeft(scrollLeft + columnWidth + gapWidth)
      }
    },
    [subjectType, columnWidth, gapWidth, itemsCount, pinnedCount, scrollLeft]
  )

  const itemPick = useCallback(
    ({ uid }) => {
      const itemIndex = items.findIndex(v => uid === v.uid) ?? -1
      if (items[itemIndex]) {
        if (!items[itemIndex].pinned || !fixPinned) {
          const newScrollLeft = (columnWidth + gapWidth) * (itemIndex - pinnedCount)
          setScrollLeft(newScrollLeft)
        }
        startBlinking(uid)
      }
    },
    [items, columnWidth, gapWidth, pinnedCount, fixPinned, startBlinking]
  )

  /**
   * Remove subject from item list
   */
  const itemDelete = useCallback(
    ({ uid, left, right }) => {
      // Dispatch item delete event
      poolDispatch(makeItemDeleteEventAction(uid))
      // Scroll item left
      if (left) {
        setScrollLeft(scrollLeft + columnWidth + gapWidth)
      } else if (right) {
        setScrollLeft(scrollLeft - columnWidth - gapWidth)
      }
    },
    [columnWidth, gapWidth, scrollLeft]
  )

  /**
   * Process subject drugging events
   */
  const itemDrag = useCallback(
    ({ action, index, uid, data }) => {
      // Autoscroll when dragging item
      if ('start' === action) {
        setDragItemIndex(index)
      } else if ('end' === action) {
        setDragItemIndex(-1)
      } else if ('enter' === action && dragItemIndex !== index) {
        if (itemTo && itemTo == index) {
          setScrollLeft((columnWidth + gapWidth) * (itemFrom + 1))
        } else if (itemFrom && itemFrom == index) {
          setScrollLeft((columnWidth + gapWidth) * (itemFrom - fixedColumns))
        }
      }
      // Dispatch drag event
      if (['start', 'end'].includes(action)) {
        poolDispatch(makeItemDragTrackStateEventAction(action, index, uid))
      } else if (['enter', 'leave'].includes(action)) {
        poolDispatch(makeItemDragListenStateEventAction(action, index, uid))
      } else if (['drop'].includes(action)) {
        poolDispatch(makeItemDragListenStateEventAction(action, index, uid, data?.index, data?.uid))
      }
    },
    [columnWidth, dragItemIndex, fixedColumns, gapWidth, itemFrom, itemTo]
  )

  /**
   * Updates local items hover state
   */
  const itemHover = useCallback(({ action, index }) => {
    if ('enter' === action) {
      setHoverItemIndex(index)
    } else if ('move' === action) {
      setHoverItemIndex(index)
    } else {
      setHoverItemIndex(-1)
    }
  }, [])

  /**
   * Show/Hide row averages
   */
  const showRowAveragesChange = useCallback(
    event => {
      if (event.showRowAverages) {
        itemAdd({ uid: defaultAveragesUids[0] })
      } else {
        itemDelete({ uid: defaultAveragesUids[0], right: true })
      }
    },
    [itemAdd, itemDelete]
  )

  // Attributes

  /**
   * Fires on attribute drag event.
   * event.attribute contains dragging attribute
   * event.from field contains source attribute name
   * event.to field contains target attribute name
   */
  const attributeDrag = useCallback(
    event => {
      switch (event.action) {
        case 'start': {
          // Set dragging attribute
          setDragAttribute(event.data)
          break
        }
        case 'end': {
          // Release dragging attribute
          setDragAttribute(null)
          break
        }
        case 'enter': {
          if (dragAttribute?.attribute !== event.data.attribute) {
            // Swap attributes when dragging attribute hovers on target attribute
            dragAttribute &&
              attributeReplace({
                from: { attribute: dragAttribute.attribute, name: dragAttribute.section, target: dragAttribute.target },
                to: { attribute: event.data.attribute, name: event.data.section, target: event.data.target }
              })

            if (dragAttribute?.section && dragAttribute.section !== event.data.section) {
              setDragAttribute(
                dragAttribute
                  ? { ...dragAttribute, section: event.data.section }
                  : {
                      attribute: event.data.attribute,
                      section: event.data.section,
                      target: event.data.target
                    }
              )
            }
          }
          break
        }
        case 'drop': {
          const { newSection, attribute, section, target } = event.data
          if (newSection) {
            attributeReplace({
              from: dragAttribute
                ? { ...dragAttribute, name: dragAttribute.section }
                : { attribute, name: section, target: target },
              to: { attribute, name: newSection, target: target }
            })
            break
          }
          setDragAttribute(null)
          break
        }
      }
    },
    [dragAttribute]
  )

  // Others

  /**
   * Change current split
   */
  const currentSplitChange = useCallback(event => {
    setScrollLeft(0)
    poolDispatch(makeCurrentSplitChangeEventAction(event))
  }, [])

  /**
   * Keep local showing columns interval updated after scroll
   */
  const changeShowFromTo = useCallback(event => {
    poolDispatch(changeShowFromToEventAction(event))
  }, [])

  /**
   * Updates scrollLeft property when virtual scroll (ScrollBar) changes horizontal position of data table (Table).
   * This property is used by Table component to adjust current virtual scroll position.
   */
  const tableScroll = useCallback(
    event => {
      if (event.scrollLeft >= 0 && event.scrollLeft !== scrollLeft) {
        setScrollLeft(event.scrollLeft)
      }
    },
    [scrollLeft]
  )

  // State events

  const topicKey = trackAttributes[trackTarget]
  const topicAttribute = Object.values(attributes).find(v => topicKey === v.key)
  const topic = topicAttribute?.text

  const splitLength = currentSplit ? splitOptions.length : 0

  const isUsingDynamicColumns = hideBlankColumns && 'All' === currentPreset

  const hasData = useMemo(
    () =>
      !hasPportalFilters
        ? false
        : isUsingDynamicColumns
        ? items?.filter(v => !isRowAveragesUid(v.uid)).length > 0
        : selectedSubjects > 0,
    [hasPportalFilters, isUsingDynamicColumns, items, selectedSubjects]
  )

  return {
    mounted: mounted.current,
    dataFetched,
    serverError,
    hasData,
    editor: currentEditor,
    scrollLeft,
    dragAttribute,
    dragItemIndex,
    hoverItemIndex,
    fixedColumns,
    fixedColumnsWidth,
    splitLength,
    topic,
    currentSplit,
    swap,
    editorChange: handleEditorChange,
    tagsUpdate,
    itemAdd,
    itemPick,
    itemSelect: dispatchItemSelect,
    itemPin,
    itemDelete,
    itemDrag,
    itemHover,
    jumpToSubject,
    tableScroll,
    sortByAttributeChange,
    attributeDelete,
    attributeAdd,
    attributeDrag,
    metricsChange,
    currentWaveChange,
    currentPresetChange,
    showSectionChange,
    showRowAveragesChange,
    confidenceLevelChange,
    secondaryConfidenceLevelChange,
    chartScaleChange,
    chartTypeChange,
    colorsChange,
    trackTargetChange,
    trackAttributesChange,
    chartLegendClick,
    currentSplitChange,
    swapChange,
    changeShowFromTo,
    refreshComponent,
    elementToggle,
    imageUpload
  }
}

export default useEvents
